import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Bike, StravaConnectResponse, StravaStatus } from '@klunkers/shared'
import { api } from '@/api/http'
import { useGarageStore } from './garage'
import { useRidesStore } from './rides'

/** The rider's Strava connection. Rides themselves arrive through ride-sync-agent. */
export const useStravaStore = defineStore('strava', () => {
  const status = ref<StravaStatus | null>(null)

  async function load() {
    status.value = await api('/strava/status', { schema: StravaStatus })
    return status.value
  }

  /** Send the rider to Strava to approve Klunkers. They come back to Settings. */
  async function connect() {
    const { url } = await api('/strava/connect', { method: 'POST', schema: StravaConnectResponse })
    // On a phone this opens the system browser (Capacitor sends other sites
    // there); Strava returns the rider to klunkers.bike/settings.
    window.location.assign(url)
  }

  async function sync() {
    await api('/strava/sync', { method: 'POST' })
    await load()
  }

  async function disconnect() {
    await api('/strava', { method: 'DELETE' })
    await load()
  }

  /** Which Klunkers bike a Strava bike is. `bikeId` null unmaps it. */
  async function mapGear(gearId: string, bikeId: string | null) {
    const garage = useGarageStore()
    const current = garage.bikes.find((b) => b.strava_gear_id === gearId)
    if (current && current.id !== bikeId) {
      await api(`/bikes/${current.id}/strava-gear`, {
        method: 'PUT',
        body: { gear_id: null },
        schema: Bike,
      })
    }
    if (bikeId) {
      await api(`/bikes/${bikeId}/strava-gear`, {
        method: 'PUT',
        body: { gear_id: gearId },
        schema: Bike,
      })
    }
    await garage.load()
    // Unassigned rides on that gear have just moved onto the bike.
    useRidesStore().stale()
  }

  return { status, load, connect, sync, disconnect, mapGear }
})
